<script setup lang="ts">
import { menu } from '~/data/header';

const modalsStore = useModalsStore();
const defaultsStore = useDefaultsStore();
const { isSticky, isHidden } = useHeader();
const device = useDevice();

const headerRef = useTemplateRef('headerRef');
const bannerRef = useTemplateRef('bannerRef');
const headerSize = useElementSize(headerRef);
const bannerSize = useElementSize(bannerRef);
const isMobileMenuOpen = ref(false);
let isScrollLock: WritableComputedRef<boolean>;

if (import.meta.client) {
  isScrollLock = useScrollLock(document.body);
};

const mobileMenuHeight = computed(() => {
    if (isSticky.value) return defaultsStore.headerHeight;
    return defaultsStore.headerHeight + defaultsStore.bannerHeight
});


function closeMobileMenu() {
  if (device.isDesktop.value) return;
  isMobileMenuOpen.value = false;
  isScrollLock.value = false;
};

function handleToggleMobileMenu() {
  if (device.isDesktop.value) return;
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  isScrollLock.value = isMobileMenuOpen.value;
};

function handleCloseMobileMenu() {
  closeMobileMenu();
};

function handleClickToCall() {
  closeMobileMenu();
  window.location.href = 'tel:02182800000';
};

function handleOpenModal() {
  closeMobileMenu();
  modalsStore.openModal('CTA');
};


onMounted(() => {
  defaultsStore.headerHeight = headerSize.height.value;
  defaultsStore.bannerHeight = bannerSize.height.value;
});
</script>

<template>
  <header>
    <HeadersMainBanner ref="bannerRef" class="relative z-101" />
    <!-- This div is in sticky mode to fill the empty header space -->
    <div v-if="isSticky" class="w-full" :style="{ height: defaultsStore.headerHeight + 'px' }" />
    <!---->
    <div
      ref="headerRef"
      class="w-full h-16 bg-white shadow-gray-s transition-transform duration-300 z-1000000000"
      :class="[
        isSticky ? 'fixed! top-0 left-0' : 'relative',
        isHidden ? '-translate-y-full' : 'translate-y-0',
      ]"
    >
      <div class="lg:relative w-full h-full container flex items-center justify-between">
          <div class="h-full flex items-center gap-2.5 min-[375px]:gap-4 lg:gap-15">
              <DevicesMobile>
                  <IconsHamburgerMenu :isOpen="isMobileMenuOpen" @click="handleToggleMobileMenu" />
              </DevicesMobile>
              <NuxtLink 
                to="/" 
                class="flex items-center gap-1.5 cursor-pointer text-primary-600 select-none"
                @click="handleCloseMobileMenu"
              >
                  <Icon name="nava-icon:navatel-bold" class="w-8! h-8! lg:w-10! lg:h-10!" />
                  <span class="text-[1.25rem] lg:text-[1.375rem] font-bold">نواتل</span>
              </NuxtLink>
              <div 
                  class="menu"
                  :class="isMobileMenuOpen && '-translate-x-full'"
                  :style="{ 
                      top: `${ defaultsStore.headerHeight }px`,
                      height: `calc(100vh - ${ mobileMenuHeight }px)`,
                  }"
              >
                <DevicesMobile class="w-full h-full">
                  <div class="w-full h-full flex flex-col">
                    <AccordionGroup :defaultOpenFirst="false">
                        <HeadersMainSubMenuMobile
                            v-for="item in menu"
                            :key="item.label"
                            :item
                            @clickLink="handleToggleMobileMenu"
                        />
                    </AccordionGroup>
                    <div class="w-full flex flex-col gap-5 mt-auto">
                        <Button size="Small" @click="handleOpenModal">مشاوره رایگان</Button>
                        <Button 
                          size="Small"
                          type="Outline" 
                          to="https://cp.navatel.ir/auth/login" 
                          target="_blank" 
                          link
                        >
                          ورود به پنل مدیریتی
                        </Button>
                        <Button 
                          size="Small" 
                          type="Secondary"
                          to="/downloads/" 
                          link
                          @click="handleCloseMobileMenu"
                        >
                            دانلود اپلیکیشن
                        </Button>
                    </div>
                  </div>
                </DevicesMobile>
                <DevicesDesktop class="h-full">
                  <div class="h-full flex items-center gap-4">
                    <HeadersMainSubMenuDesktop
                        v-for="item in menu"
                        :key="item.label"
                        :item
                    />
                  </div>
                </DevicesDesktop>
              </div>
          </div>
          <DevicesMobile>
              <div class="flex items-center gap-2 min-[375px]:gap-4">
                  <Button 
                      type="Thirdly"
                      size="Small"
                      :appendIcon="{ name: 'solar:phone-rounded-linear'}"
                      class="phone-ringing px-0! [&>.btn-slot-parent]:gap-1"
                      @click="handleClickToCall"
                  >
                      0000 80 82 021
                  </Button>
                  <Button 
                    size="Small" 
                    to="/online-shopping/buy/" 
                    link
                    @click="handleCloseMobileMenu"
                  >
                      خرید
                  </Button>
              </div>
          </DevicesMobile>
          <DevicesDesktop>
              <div class="flex items-center gap-5">
                  <Button 
                      type="Thirdly"
                      size="Large"
                      :appendIcon="{ name: 'solar:phone-rounded-linear'}"
                      class="phone-ringing px-0! [&>.btn-slot-parent]:gap-1"
                      @click="handleClickToCall"
                  >
                      0000 80 82 021
                  </Button>
                  <Button type="Outline" to="https://cp.navatel.ir/auth/login" target="_blank" link>ورود به پنل مدیریتی</Button>
                  <Button to="/online-shopping/buy/" link>خرید آنلاین</Button>
              </div>
          </DevicesDesktop>
      </div>
    </div>
  </header>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.menu {
    @apply
    absolute w-screen -right-full flex justify-center bg-white z-1 px-4 pt-6 pb-25 overflow-auto
    transition-transform duration-300 before:content-[''] before:absolute before:w-62.5 
    before:h-75 before:bg-primary-100 before:rounded-full before:blur-2xl before:top-16 before:-z-1
    lg:static! lg:w-auto! lg:h-full! lg:p-0! lg:before:hidden!
}

:deep(.phone-ringing .iconify) {
    @apply
    animate-phone-ring
}
</style>